/**
 * Vaults de ORGANIZACIÓN: reparto de la clave del vault por miembro.
 *
 * La clave del vault de equipo se genera UNA vez y se "reparte" cifrándola a la
 * clave pública ECDH de cada miembro (ECIES). Cada miembro la abre con su clave
 * privada. El servidor solo custodia los `sealedVaultKey` (opacos) y no puede
 * abrir ninguno: no tiene ninguna clave privada.
 *
 * - Añadir miembro: sellar la clave del vault a su clave pública.
 * - Revocar miembro + ROTAR: generar clave nueva (época+1), repartir solo a los
 *   miembros que quedan, y re-cifrar los items. El revocado, aunque tenga su
 *   antiguo `sealedVaultKey`, no puede abrir los items nuevos (época nueva).
 */

import { openWithPrivateKey, type SealedToPublicKey, sealToPublicKey } from "./asymmetric";
import { createVaultKey } from "./vault";
import type { VaultMembership } from "./types";

export interface OrgMemberPublic {
  readonly userId: string;
  readonly publicKey: string;
  readonly role: VaultMembership["role"];
}

function vaultKeyAad(vaultId: string, epoch: number): string {
  return `boveda:org-vault-key:v1:${vaultId}:${epoch}`;
}

export interface CreatedOrgVault {
  readonly vaultId: string;
  readonly keyEpoch: number;
  /** Clave del vault en claro — SOLO en el cliente del creador. */
  readonly vaultKey: Uint8Array;
  /** Reparto listo para subir (blobs opacos). */
  readonly memberships: VaultMembership[];
}

/** Crea un vault de organización y reparte su clave a los miembros dados. */
export async function createOrgVault(
  vaultId: string,
  members: OrgMemberPublic[],
): Promise<CreatedOrgVault> {
  const vaultKey = createVaultKey();
  const keyEpoch = 1;
  const memberships = await distributeVaultKey(vaultId, keyEpoch, vaultKey, members);
  return { vaultId, keyEpoch, vaultKey, memberships };
}

/** Sella la clave del vault a cada miembro (usar al crear, añadir o rotar). */
export async function distributeVaultKey(
  vaultId: string,
  keyEpoch: number,
  vaultKey: Uint8Array,
  members: OrgMemberPublic[],
): Promise<VaultMembership[]> {
  const aad = vaultKeyAad(vaultId, keyEpoch);
  const out: VaultMembership[] = [];
  for (const m of members) {
    const sealed = await sealToPublicKey(m.publicKey, vaultKey, aad);
    out.push({
      vaultId,
      userId: m.userId,
      role: m.role,
      sealedVaultKey: JSON.stringify(sealed),
      keyEpoch,
    });
  }
  return out;
}

/** Un miembro abre la clave del vault con su clave privada. */
export async function openMembership(
  privateKeyB64: string,
  membership: VaultMembership,
): Promise<Uint8Array> {
  // Trust boundary: la membresía la escribió el flujo de reparto de esta app;
  // si el blob estuviera corrupto, el AEAD open fallará igualmente.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const sealed = JSON.parse(membership.sealedVaultKey) as SealedToPublicKey;
  return openWithPrivateKey(
    privateKeyB64,
    sealed,
    vaultKeyAad(membership.vaultId, membership.keyEpoch),
  );
}

export interface RotatedOrgVault {
  readonly keyEpoch: number;
  readonly vaultKey: Uint8Array;
  readonly memberships: VaultMembership[];
}

/**
 * Rota la clave del vault (p. ej. tras revocar a alguien). `remainingMembers`
 * son los que conservan acceso. Devuelve la clave nueva y el reparto nuevo; el
 * llamador debe re-cifrar los items con `reencryptItem` a la nueva clave.
 */
export async function rotateOrgVault(
  vaultId: string,
  previousEpoch: number,
  remainingMembers: OrgMemberPublic[],
): Promise<RotatedOrgVault> {
  const keyEpoch = previousEpoch + 1;
  const vaultKey = createVaultKey();
  const memberships = await distributeVaultKey(vaultId, keyEpoch, vaultKey, remainingMembers);
  return { keyEpoch, vaultKey, memberships };
}
